import React from 'react';
import GridOverlay from './GridOverlay';
import { HERO_MEDIA } from '../config/media';

/**
 * Brand-correct hero strip used on every route this phase.
 * If the keyed media has a `src`, it renders the image; otherwise a
 * graphite placeholder with grid overlay so layout is verifiable.
 */
export const HeroPlaceholder = ({
    mediaKey,
    imageUrl,
    eyebrow,
    title,
    subtitle,
    height = 'h-[420px] md:h-[520px]',
}) => {
    const media = (mediaKey && HERO_MEDIA[mediaKey]) || null;
    // Phase 2.6: `imageUrl` (from CMS hero.image_url) overrides the
    // mediaKey-mapped src. The alt text falls through to media.alt when
    // present so published imagery still has an accessible description.
    const resolvedSrc = imageUrl || (media && media.src) || '';
    const alt = (media && media.alt) || title || '';
    const hasImage = !!resolvedSrc;

    return (
        <section
            data-testid={`hero-${mediaKey || 'default'}`}
            className={`relative w-full overflow-hidden bg-brand-surface ${height}`}
        >
            {hasImage ? (
                <img
                    src={resolvedSrc}
                    alt={alt}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                />
            ) : null}

            {/* Directional dark gradient - matches HeroCarousel treatment.
                Top ~55% dark preserves photo punch; bottom ~92% dark ensures
                hero H1 + subtitle read cleanly against complex imagery. */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        'linear-gradient(to bottom, rgba(11,11,13,0.55) 0%, rgba(11,11,13,0.35) 25%, rgba(11,11,13,0.75) 55%, rgba(11,11,13,0.92) 100%)',
                    opacity: 1,
                }}
            />
            {/* Extra bottom darken on placeholder (no image) so eyebrow/h1 sit
                on a graphite plate rather than full-grid. */}
            {!hasImage && (
                <div className="absolute inset-0 bg-brand-base/40" />
            )}

            {/* engineering grid sits on top of gradient, per spec */}
            <GridOverlay size={56} opacity={hasImage ? 0.14 : 0.18} />

            {/* faint vignette */}
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        'radial-gradient(ellipse at 30% 30%, rgba(212,162,43,0.06), transparent 55%)',
                }}
            />

            <div
                className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-12 md:px-10 md:pb-16"
                style={{ textShadow: '0 2px 8px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.5)' }}
            >
                {eyebrow ? (
                    <span className="eyebrow mb-5" data-testid="hero-eyebrow">
                        {eyebrow}
                    </span>
                ) : null}
                {title ? (
                    <h1
                        className="h-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl font-extrabold"
                        data-testid="hero-title"
                    >
                        {title}
                    </h1>
                ) : null}
                {subtitle ? (
                    <p
                        className="mt-5 max-w-2xl text-base md:text-lg font-medium text-brand-text"
                        data-testid="hero-subtitle"
                    >
                        {subtitle}
                    </p>
                ) : null}
            </div>
        </section>
    );
};

export default HeroPlaceholder;
